import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import {
  Search, ArrowUp, ArrowDown, ArrowUpDown, Users, Crown,
  UserCog, Briefcase, Wrench, FlaskConical, ExternalLink,
  Trash2, RotateCcw, AlertTriangle,
} from "lucide-react";

const ROLE_LABEL = {
  admin: "Administrator",
  sales_director: "Vertriebsdirektor",
  sales_manager: "Vertriebsleiter",
  branch_manager: "Niederlassungsleiter",
  sales_rep: "Vertrieb",
  installer: "Installation",
};
const ROLE_OPTIONS = ["", "admin", "sales_director", "sales_manager", "sales_rep", "agent", "installer"];
const STATUS_OPTIONS = ["", "active", "pending", "blocked", "deleted"];

const roleIcon = (r) => {
  if (r === "admin" || r === "sales_director") return <Crown size={11} className="text-amber-400" />;
  if (r === "sales_manager") return <UserCog size={11} className="text-sky-400" />;
  if (r === "installer") return <Wrench size={11} className="text-violet-400" />;
  return <Briefcase size={11} className="text-emerald-400" />;
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" });
  } catch { return "—"; }
};

const formatEur = (n) => {
  if (!n) return "—";
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
};

// --- Sortable Column Header ---
const SortHeader = ({ label, field, sort, onSort, align = "left", className = "" }) => {
  const active = sort.field === field;
  const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th
      data-testid={`users-table-sort-${field}`}
      onClick={() => onSort(field)}
      className={`px-3 py-2.5 text-xs font-bold uppercase tracking-wider cursor-pointer select-none hover:bg-zinc-800/50 transition ${
        active ? "text-[#00E575]" : "text-zinc-400"
      } text-${align} ${className}`}
    >
      <span className="inline-flex items-center gap-1.5">
        {label}
        <Icon size={11} className={active ? "" : "opacity-40"} />
      </span>
    </th>
  );
};

export default function AdminUsersTable() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sort, setSort] = useState({ field: "last_name", dir: "asc" });

  useEffect(() => {
    setLoading(true);
    const params = includeDeleted ? "?include_deleted=true" : "";
    api.get(`/users/with-kpis${params}`)
      .then((r) => setUsers(r.data || []))
      .catch((e) => toast.error(e.response?.data?.detail || "Fehler beim Laden"))
      .finally(() => setLoading(false));
  }, [includeDeleted]);

  const handleSort = (field) => {
    setSort((s) => s.field === field ? { field, dir: s.dir === "asc" ? "desc" : "asc" } : { field, dir: "asc" });
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = users.filter((u) => {
      if (roleFilter && u.role !== roleFilter) return false;
      if (statusFilter && u.status !== statusFilter) return false;
      if (q) {
        const hay = `${u.first_name || ""} ${u.last_name || ""} ${u.email || ""} ${u.phone || ""} ${u.company_name || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const dir = sort.dir === "asc" ? 1 : -1;
    list = [...list].sort((a, b) => {
      const av = a[sort.field];
      const bv = b[sort.field];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv), "de") * dir;
    });
    return list;
  }, [users, search, roleFilter, statusFilter, sort]);

  // Aggregate stats above the table
  const stats = useMemo(() => ({
    total: filtered.length,
    active: filtered.filter((u) => u.status === "active").length,
    pending: filtered.filter((u) => u.status === "pending").length,
    test: filtered.filter((u) => u.is_test_account).length,
    revenue: filtered.reduce((s, u) => s + (u.kpi_revenue || 0), 0),
    projects: filtered.reduce((s, u) => s + (u.kpi_project_count || 0), 0),
  }), [filtered]);

  const openEdit = (u) => navigate(`/admin/users?focus=${u.id}`);

  // --- Per-row Soft-Delete / Hard-Delete / Restore ---
  const handleSoftDelete = async (e, u) => {
    e.stopPropagation();
    if (!window.confirm(
      `Benutzer "${u.first_name} ${u.last_name}" wirklich deaktivieren?\n\n`
      + "Das Konto wird auf 'gelöscht' gesetzt — die Daten bleiben für Audit-Zwecke in der Datenbank "
      + "und können später wiederhergestellt werden."
    )) return;
    try {
      await api.post(`/users/${u.id}/soft-delete`);
      toast.success(`${u.first_name} ${u.last_name} deaktiviert`);
      setUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, status: "deleted" } : x));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Deaktivieren fehlgeschlagen");
    }
  };

  const handleRestore = async (e, u) => {
    e.stopPropagation();
    try {
      const r = await api.post(`/users/${u.id}/restore`);
      toast.success(`${u.first_name} ${u.last_name} wiederhergestellt (${r.data.restored_to_status})`);
      setUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, status: r.data.restored_to_status } : x));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Wiederherstellen fehlgeschlagen");
    }
  };

  const handleHardDelete = async (e, u) => {
    e.stopPropagation();
    const confirmText = `${u.first_name} ${u.last_name}`;
    const typed = window.prompt(
      `⚠️ ENDGÜLTIG LÖSCHEN ⚠️\n\n`
      + `Diese Aktion entfernt den Benutzer komplett aus der Datenbank und kann NICHT rückgängig gemacht werden.\n\n`
      + `Zum Bestätigen bitte den vollständigen Namen eingeben:\n"${confirmText}"`
    );
    if (typed === null) return;
    if (typed.trim() !== confirmText) {
      toast.error("Name stimmt nicht überein — Löschung abgebrochen");
      return;
    }
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`${confirmText} endgültig gelöscht`);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      <AppHeader title="Benutzer (Tabelle)" back="/dashboard" />
      <main className="max-w-[1600px] mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          <div className="h-12 w-12 rounded-2xl bg-sky-500/15 flex items-center justify-center">
            <Users className="text-sky-400" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <h1 className="font-display text-2xl font-semibold">Benutzer-Tabelle</h1>
            <p className="text-sm text-zinc-300 font-medium">
              Vollständige Daten-Sicht mit Sortierung, Filter und KPIs.
              Klick auf eine Zeile öffnet die Bearbeitung.
            </p>
          </div>
          <button
            data-testid="users-table-go-classic"
            onClick={() => navigate("/admin/users")}
            className="h-10 px-4 rounded-xl bg-zinc-800 text-zinc-200 text-sm font-semibold hover:bg-zinc-700 transition inline-flex items-center gap-1.5"
          >
            <ExternalLink size={14} /> Klassische Verwaltung
          </button>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-5">
          {[
            { label: "Gesamt", value: stats.total, color: "text-zinc-100" },
            { label: "Aktiv", value: stats.active, color: "text-[#00E575]" },
            { label: "Pending", value: stats.pending, color: "text-amber-400" },
            { label: "Test-Konten", value: stats.test, color: "text-fuchsia-400" },
            { label: "Projekte (Σ)", value: stats.projects, color: "text-sky-400" },
            { label: "Umsatz (Σ)", value: formatEur(stats.revenue), color: "text-emerald-300" },
          ].map((s) => (
            <div key={s.label} data-testid={`users-table-stat-${s.label}`} className="bg-[#18181B] border border-zinc-800 rounded-xl px-3 py-2">
              <div className="text-[12px] uppercase tracking-wider text-zinc-300 font-semibold">{s.label}</div>
              <div className={`text-lg font-bold mt-0.5 ${s.color}`}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              data-testid="users-table-search"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Suche: Name, E-Mail, Telefon, Firma…"
              className="h-10 w-full bg-[#18181B] border border-zinc-800 rounded-xl pl-9 pr-3 text-sm placeholder:text-zinc-500"
            />
          </div>
          <select
            data-testid="users-table-filter-role"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="h-10 bg-[#18181B] border border-zinc-800 rounded-xl px-3 text-sm"
          >
            {ROLE_OPTIONS.map((r) => (
              <option key={r || "all"} value={r}>{r ? ROLE_LABEL[r] : "Alle Rollen"}</option>
            ))}
          </select>
          <select
            data-testid="users-table-filter-status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-10 bg-[#18181B] border border-zinc-800 rounded-xl px-3 text-sm"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s || "all"} value={s}>{
                s === "" ? "Alle Status"
                : s === "active" ? "Aktiv"
                : s === "pending" ? "Pending"
                : s === "blocked" ? "Gesperrt"
                : "Gelöscht"
              }</option>
            ))}
          </select>
          <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-300 px-3 py-2 rounded-xl bg-zinc-900 border border-zinc-800 cursor-pointer">
            <input
              data-testid="users-table-include-deleted"
              type="checkbox"
              checked={includeDeleted}
              onChange={(e) => setIncludeDeleted(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-zinc-600 bg-zinc-900"
            />
            Gelöschte einbeziehen
          </label>
        </div>

        {/* Table */}
        <div className="bg-[#18181B] border border-zinc-800 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="text-zinc-300 text-center font-medium py-12">Lade…</div>
          ) : filtered.length === 0 ? (
            <div className="text-zinc-300 text-center font-medium py-12">Keine Benutzer gefunden.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="users-table">
                <thead className="bg-zinc-900/60 border-b border-zinc-800">
                  <tr>
                    <SortHeader label="Nachname" field="last_name" sort={sort} onSort={handleSort} />
                    <SortHeader label="Vorname" field="first_name" sort={sort} onSort={handleSort} />
                    <SortHeader label="Rolle" field="role" sort={sort} onSort={handleSort} />
                    <SortHeader label="Status" field="status" sort={sort} onSort={handleSort} />
                    <SortHeader label="E-Mail" field="email" sort={sort} onSort={handleSort} />
                    <SortHeader label="Telefon" field="phone" sort={sort} onSort={handleSort} />
                    <SortHeader label="PLZ" field="zip" sort={sort} onSort={handleSort} />
                    <SortHeader label="Ort" field="city" sort={sort} onSort={handleSort} />
                    <SortHeader label="Firma" field="company_name" sort={sort} onSort={handleSort} />
                    <SortHeader label="USt-ID" field="vat_id" sort={sort} onSort={handleSort} />
                    <SortHeader label="Projekte" field="kpi_project_count" sort={sort} onSort={handleSort} align="right" />
                    <SortHeader label="Signed" field="kpi_signed_offers" sort={sort} onSort={handleSort} align="right" />
                    <SortHeader label="Umsatz" field="kpi_revenue" sort={sort} onSort={handleSort} align="right" />
                    <SortHeader label="Offen" field="kpi_open_tasks" sort={sort} onSort={handleSort} align="right" />
                    <SortHeader label="Erstellt" field="created_at" sort={sort} onSort={handleSort} />
                    <SortHeader label="Letzte Akt." field="kpi_last_activity" sort={sort} onSort={handleSort} />
                    <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wider text-zinc-400 text-right whitespace-nowrap">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((u, idx) => (
                    <tr
                      key={u.id}
                      data-testid={`users-table-row-${u.id}`}
                      onClick={() => openEdit(u)}
                      className={`border-b border-zinc-800/50 hover:bg-zinc-800/40 cursor-pointer transition ${
                        idx % 2 === 0 ? "bg-transparent" : "bg-zinc-900/20"
                      } ${u.status === "deleted" ? "opacity-50" : ""}`}
                    >
                      <td className="px-3 py-2 font-medium text-zinc-100 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          {u.last_name || "—"}
                          {u.is_test_account && (
                            <span title="Test-Konto" className="inline-flex items-center px-1 py-0.5 rounded text-[9px] font-bold bg-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-500/40">
                              <FlaskConical size={8} />
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-zinc-200 whitespace-nowrap">{u.first_name || "—"}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1 text-xs text-zinc-300">
                          {roleIcon(u.role)} {ROLE_LABEL[u.role] || u.role}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[12px] font-bold uppercase tracking-wider ${
                          u.status === "active" ? "bg-[#00E575]/15 text-[#00E575]"
                          : u.status === "pending" ? "bg-amber-500/15 text-amber-400"
                          : u.status === "blocked" ? "bg-red-500/15 text-red-400"
                          : "bg-zinc-700 text-zinc-400"
                        }`}>{u.status}</span>
                      </td>
                      <td className="px-3 py-2 text-zinc-300 whitespace-nowrap max-w-[180px] truncate" title={u.email}>{u.email || "—"}</td>
                      <td className="px-3 py-2 font-mono text-xs text-zinc-300 whitespace-nowrap">{u.phone || "—"}</td>
                      <td className="px-3 py-2 font-mono text-xs text-zinc-300 whitespace-nowrap">{u.zip || "—"}</td>
                      <td className="px-3 py-2 text-zinc-300 whitespace-nowrap">{u.city || "—"}</td>
                      <td className="px-3 py-2 text-zinc-300 whitespace-nowrap max-w-[150px] truncate" title={u.company_name}>{u.company_name || "—"}</td>
                      <td className="px-3 py-2 font-mono text-xs text-zinc-400 whitespace-nowrap">{u.vat_id || "—"}</td>
                      <td className="px-3 py-2 text-right font-mono text-sky-300">{u.kpi_project_count || 0}</td>
                      <td className="px-3 py-2 text-right font-mono text-emerald-300">{u.kpi_signed_offers || 0}</td>
                      <td className="px-3 py-2 text-right font-mono text-emerald-200 whitespace-nowrap">{formatEur(u.kpi_revenue)}</td>
                      <td className="px-3 py-2 text-right font-mono text-amber-300">{u.kpi_open_tasks || 0}</td>
                      <td className="px-3 py-2 text-xs text-zinc-400 whitespace-nowrap">{formatDate(u.created_at)}</td>
                      <td className="px-3 py-2 text-xs text-zinc-400 whitespace-nowrap">{formatDate(u.kpi_last_activity)}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-right">
                        <div className="inline-flex items-center gap-1.5">
                          {u.status === "deleted" ? (
                            <button
                              data-testid={`users-table-restore-${u.id}`}
                              title="Wiederherstellen"
                              onClick={(e) => handleRestore(e, u)}
                              className="p-1.5 rounded-md text-sky-300 hover:bg-sky-500/15 hover:text-sky-200 transition"
                            >
                              <RotateCcw size={14} />
                            </button>
                          ) : (
                            <button
                              data-testid={`users-table-soft-delete-${u.id}`}
                              title="Deaktivieren (Soft-Delete)"
                              onClick={(e) => handleSoftDelete(e, u)}
                              className="p-1.5 rounded-md text-amber-300 hover:bg-amber-500/15 hover:text-amber-200 transition"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                          <button
                            data-testid={`users-table-hard-delete-${u.id}`}
                            title="Endgültig löschen (kann nicht rückgängig gemacht werden)"
                            onClick={(e) => handleHardDelete(e, u)}
                            className="p-1.5 rounded-md text-red-400 hover:bg-red-500/15 hover:text-red-300 transition"
                          >
                            <AlertTriangle size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-xs text-zinc-300 mt-3">
          {filtered.length} von {users.length} Benutzern · Spaltenkopf klicken für Sortierung · Zeile klicken für Bearbeitung · Aktion-Spalte: <span className="text-amber-300">Soft-Delete</span> / <span className="text-sky-300">Wiederherstellen</span> / <span className="text-red-400">Endgültig löschen</span>
        </p>
      </main>
    </div>
  );
}
